import React, { useState, useEffect } from 'react'
import {
  Form,
  Button,
  Row,
  Col,
  Card,
} from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-bootstrap-typeahead'
import { Typeahead } from 'react-bootstrap-typeahead'
import { BsArrowLeft, BsCheckLg, BsXLg, BsPerson, BsCalendar, BsGenderAmbiguous, BsCardText, BsBook, BsBuilding, BsPersonBadge, BsFileEarmarkText, BsPhone, BsWhatsapp, BsEnvelope, BsGeoAlt, BsCardImage } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { FaUser, FaUserTie, FaChalkboard } from 'react-icons/fa'
import ApiEndpoints, { BASE_URL, BASE_URL_Upload } from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import { useNavigate, useLocation } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

const AddNewStudent = () => {
  const { token, logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const location = useLocation()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const [loading, setLoading] = useState(false)
  const [studentCount, setStudentCount] = useState(null)
  const [countColor, setCountColor] = useState('')
  const [newStudent, setNewStudent] = useState({
    name: '',
    dob: '',
    age: '',
    gender: '',
    father_name: '',
    mother_name: '',
    admission_date: '',
    admission_number: '',
    roll_number: '',
    course_id: '',
    class_id: '',
    id_proof_number: '',
    id_proof_type: '',
    phone: '',
    whatsapp_number: '',
    parents_number: '',
    email: '',
    address: '',
  })

  const [studentImage, setStudentImage] = useState(null)
  const [courseTouched, setCourseTouched] = useState(false)
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [selectedNewCourse, setSelectedNewCourse] = useState([])

  useEffect(() => {
    ApiEndpoints.getAllClasses().then((res) => setClasses(res.data.data))
    ApiEndpoints.getAllCourses().then((res) => setCourses(res.data.data))
  }, [])

  // Handle state from navigation (if coming from AddClass)
  useEffect(() => {
    if (location.state?.newStudent) {
      setNewStudent(location.state.newStudent)
      setStudentImage(location.state.studentImage || null)
      setSelectedNewCourse(location.state.selectedNewCourse || '')
      if (location.state.selectedNewCourse) {
        ApiEndpoints.getAllClassesByCourseId(location.state.selectedNewCourse)
          .then((res) => {
            const { data } = res
            if (data.status === false || !data.data?.length) {
              setClasses([])
            } else {
              setClasses(data.data)
            }
          })
          .catch((error) => {
            setClasses([])
          })
      }
    }
  }, [location.state])

  const handleCourseChange = (e) => {
    const courseId = e.target.value
    setCourseTouched(true)
    setSelectedNewCourse(courseId)
    setNewStudent((prev) => ({ ...prev, course_id: courseId }))
    setStudentCount(null)

    if (courseId) {
      ApiEndpoints.getAllClassesByCourseId(courseId)
        .then((res) => {
          const { data } = res
          if (data.status === false || !data.data?.length) {
            toast.error('No classes found for this course.')
            setClasses([])
            setStudentCount(null)
          } else {
            setClasses(data.data)
          }
        })
        .catch((error) => {
          if (
            error.response?.data?.message === 'No classes found for this course'
          ) {
            setStudentCount(null)
          } else {
            console.error('Error fetching classes:', error)
            toast.error('Something went wrong while fetching classes.')
          }
          setClasses([])
          setStudentCount(null)
        })
    }
  }

  const handleNavigateToCreateClass = () => {
    navigate('/AddClass', {
      state: {
        from: 'AddNewStudent',
        newStudent,
        studentImage,
        selectedNewCourse,
      },
    })
  }

  const handleClassChange = (e) => {
    const classId = e.target.value
    setNewStudent((prev) => ({ ...prev, class_id: classId }))
    setStudentCount(null)
    setCountColor('')

    if (classId) {
      fetch(
        BASE_URL + `/get/class/student/count?class_id=${classId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
        .then((response) => response.json())
        .then((data) => {
          if (
            data?.status &&
            data?.data?.current !== undefined &&
            data?.data?.maximum_limit !== undefined
          ) {
            const current = data.data.current === null ? 0 : data.data.current
            const maximum_limit = parseInt(data.data.maximum_limit, 10)
            setStudentCount({ current, maximum_limit })
            setCountColor('green')
          } else {
            setStudentCount('No data available')
            setCountColor('red')
          }
        })
        .catch((error) => {
          console.error('Error fetching student count:', error)
          setStudentCount('Error fetching data')
          setCountColor('red')
        })
    }
  }

  const calculateAge = (dob) => {
    if (!dob) return ''
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--
    }
    return age
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewStudent((prev) => ({ ...prev, [name]: value }))

    if (name === 'dob') {
      const age = calculateAge(value)
      setNewStudent((prev) => ({
        ...prev,
        dob: value,
        age: age.toString(),
      }))
    } else {
      setNewStudent((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!id) {
      toast.error('No institute selected. Please switch institute from the header first.')
      return
    }
    try {
      setLoading(true)
      let imageUrl = null

      if (studentImage) {
        if (studentImage.size > 2 * 1024 * 1024) {
          toast.error('Image size should not exceed 2MB.')
          setLoading(false)
          return
        }

        const formData = new FormData()
        formData.append('image', studentImage)
        formData.append('folder', 'student')

        const imageResponse = await axios.post(
          BASE_URL + '/upload-image',
          formData,
        )
        imageUrl = BASE_URL_Upload + `/${imageResponse.data.path}`
      }
      const studentData = {
        ...newStudent,
        student_image: imageUrl,
        institute_id: Number(id),
      }
      const response = await ApiEndpoints.addStudent(studentData)
      toast.success(response.data.message)
      const studentId = response?.data?.data?.id

      // Reset the form after submission
      setNewStudent({
        name: '',
        dob: '',
        age: '',
        gender: '',
        father_name: '',
        mother_name: '',
        admission_date: '',
        admission_number: '',
        roll_number: '',
        course_id: '',
        class_id: '',
        id_proof_type: '',
        id_proof_number: '',
        phone: '',
        whatsapp_number: '',
        parents_number: '',
        email: '',
        address: '',
      })
      setStudentImage(null)
      setSelectedNewCourse('')
      setClasses([])
      setStudentCount(null)
      setCourseTouched(false)

      // Navigate to created student's profile
      navigate(`/student/profile/${studentId}`)
    } catch (error) {
      const responseData = error?.response?.data
      const validationErrors = responseData?.errors

      if (
        responseData?.status === false &&
        responseData?.message === 'Validation failed' &&
        validationErrors
      ) {
        const firstValidationMessage = Object.values(validationErrors)
          .flat()
          .find((msg) => typeof msg === 'string' && msg.trim())

        toast.error(firstValidationMessage || 'Validation failed.')
      } else {
        toast.error(
          responseData?.message || error?.response?.message || 'Something went wrong.',
        )
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <style>
        {`
          .blinking-link {
            color: blue;
            text-decoration: none;
            cursor: pointer;
            animation: blink 1s infinite;
            font-weight: bold;
          }

          @keyframes blink {
            0% { color: blue; }
            50% { color: red; }
            100% { color: blue; }
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          marginRight: '0px',
          marginBottom: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              {/* Left: Logo, Title and Back Button */}
              <div className="d-flex align-items-center gap-3">
                {/* School Logo */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#111827',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Add New Student
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            paddingLeft: isMobile ? '12px' : isCollapsed ? '32px' : '40px',
            paddingRight: isMobile ? '12px' : '32px',
            paddingTop: isMobile ? '16px' : '32px',
            paddingBottom: isMobile ? '16px' : '32px',
          }}
        >
          <Form onSubmit={handleSave}>
            <Row className="g-4">
              {/* Personal Details Card */}
              <Col md={6} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Icon */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <BsPerson size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Personal Details
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">

                    <Form.Group className="mb-3">
                      <Form.Label>Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={newStudent.name}
                        onChange={handleInputChange}
                        placeholder="Enter Student Name"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth *</Form.Label>
                      <Form.Control
                        type="date"
                        name="dob"
                        value={newStudent.dob}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Age</Form.Label>
                      <Form.Control
                        type="text"
                        name="age"
                        value={newStudent.age}
                        disabled
                        className="p-2 border rounded bg-light"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Gender *</Form.Label>
                      <Form.Select
                        name="gender"
                        value={newStudent.gender}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Father's Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="father_name"
                        value={newStudent.father_name}
                        onChange={handleInputChange}
                        placeholder="Enter Father's Name"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Mother's Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="mother_name"
                        value={newStudent.mother_name}
                        onChange={handleInputChange}
                        placeholder="Enter Mother's Name"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Admission Details Card */}
              <Col md={6} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Icon */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <BsCardText size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Admission Details
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">

                    <Form.Group className="mb-3">
                      <Form.Label>Admission Date *</Form.Label>
                      <Form.Control
                        type="date"
                        name="admission_date"
                        value={newStudent.admission_date}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Admission Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="admission_number"
                        value={newStudent.admission_number}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>

                    {/* <Form.Group className="mb-3">
                      <Form.Label>Roll Numbr (Optional)</Form.Label>
                      <Form.Control
                        type="text"
                        name="roll_number"
                        value={newStudent.roll_number}
                        onChange={handleInputChange}
                        placeholder="Enter Roll Number"
                      />
                    </Form.Group> */}

                    <Form.Group className="mb-3">
                      <Form.Label>Course *</Form.Label>
                      <div style={{ position: 'relative' }}>
                        <Typeahead
                          id="add-new-student-course"
                          labelKey="name"
                          onChange={(selected) => {
                            const course = selected[0]
                            if (course) {
                              handleCourseChange({
                                target: { value: course.id },
                              })
                            } else {
                              handleCourseChange({ target: { value: '' } })
                            }
                          }}
                          options={courses}
                          placeholder="Select Course"
                          positionFixed
                          selected={
                            selectedNewCourse
                              ? courses.filter(
                                  (c) => c.id == selectedNewCourse,
                                )
                              : []
                          }
                          inputProps={{
                            required: true,
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              minHeight: '38px',
                            },
                          }}
                        />
                      </div>
                    </Form.Group>

                    {classes.length === 0 && selectedNewCourse && (
                      <p style={{ color: 'red', letterSpacing: '1px' }}>
                        The selected course has no class. Please{' '}
                        <span
                          className="blinking-link"
                          onClick={handleNavigateToCreateClass}
                        >
                          click here
                        </span>{' '}
                        to create new class.
                      </p>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>Class *</Form.Label>
                      <Form.Select
                        name="class_id"
                        value={newStudent.class_id}
                        onChange={handleClassChange}
                        disabled={!courseTouched || classes.length === 0}
                      >
                        <option value="">Select Class</option>
                        {classes.map((clas) => (
                          <option key={clas.id} value={clas.id}>
                            {clas.class_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    {studentCount !== null &&
                      studentCount !== 'No data available' && (
                        <div
                          style={{
                            color: countColor,
                            marginBottom: '15px',
                            fontWeight: 'bold',
                            letterSpacing: '1px',
                            padding: '10px',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '8px',
                          }}
                        >
                          {studentCount.current === null
                            ? `There are still ${studentCount.maximum_limit} seats available in this class.`
                            : studentCount.current === studentCount.maximum_limit
                            ? 'There are no more seats available in this class.'
                            : `There are ${
                                studentCount.maximum_limit - studentCount.current
                              } seats left in this class.`}
                        </div>
                      )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4 mt-0">
              {/* ID Proof Details Card */}
              <Col md={6} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Icon */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <BsPersonBadge size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        ID Proof Details
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">

                    <Form.Group className="mb-3">
                      <Form.Label>ID Type</Form.Label>
                      <Form.Select
                        name="id_proof_type"
                        value={newStudent.id_proof_type}
                        onChange={handleInputChange}
                      >
                        <option value="">Select</option>
                        <option value="aadhar card">Aadhaar</option>
                        <option value="voter card">Voter ID</option>
                        <option value="license">License</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>ID Proof Number</Form.Label>
                      <Form.Control
                        type="number"
                        name="id_proof_number"
                        value={newStudent.id_proof_number}
                        onChange={handleInputChange}
                        placeholder="Enter ID Proof Number"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Contact Details Card */}
              <Col md={6} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Icon */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <BsPhone size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Contact Details
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">

                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={newStudent.phone}
                        onChange={handleInputChange}
                        placeholder="Enter Phone Number"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>WhatsApp Number (Optional)</Form.Label>
                      <Form.Control
                        type="text"
                        name="whatsapp_number"
                        value={newStudent.whatsapp_number}
                        onChange={handleInputChange}
                        placeholder="Enter WhatsApp Number"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Parent's Phone</Form.Label>
                      <Form.Control
                        type="text"
                        name="parents_number"
                        value={newStudent.parents_number}
                        onChange={handleInputChange}
                        placeholder="Enter Parent's Phone Number"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={newStudent.email}
                        onChange={handleInputChange}
                        placeholder="Enter Email"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4 mt-0">
              {/* Address Card */}
              <Col md={6} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Icon */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <BsGeoAlt size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Address
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-0">
                      <Form.Label>Address *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="address"
                        value={newStudent.address}
                        onChange={handleInputChange}
                        placeholder="Enter Address"
                        required
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Student Image Card */}
              <Col md={6} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Icon */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <BsCardImage size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Student Image
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-0">
                      <Form.Label>Student Image (PNG/JPG/JPEG)</Form.Label>
                      <div className="d-flex align-items-center gap-3 mt-2">
                        <Form.Control
                          type="file"
                          accept="image/png, image/jpeg, image/jpg"
                          onChange={(e) => setStudentImage(e.target.files[0])}
                        />
                        {studentImage && (
                          <img
                            src={URL.createObjectURL(studentImage)}
                            alt="Preview"
                            style={{
                              width: 100,
                              height: 100,
                              objectFit: 'cover',
                              borderRadius: 8,
                              border: '1px solid #ccc',
                            }}
                          />
                        )}
                      </div>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

                <div className="d-flex justify-content-end gap-3 mt-4 pt-3" style={{ borderTop: '1px solid #E5E7EB' }}>
                  <Button
                    onClick={() => navigate('/students')}
                    disabled={loading}
                    style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #D1D3D4',
                      color: '#6b7280',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontWeight: '600',
                      fontSize: '15px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      minWidth: '140px',
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.backgroundColor = '#F3F4F6'
                        e.currentTarget.style.borderColor = '#9ca3af'
                        e.currentTarget.style.color = '#111827'
                        e.currentTarget.style.transform = 'translateY(-1px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff'
                      e.currentTarget.style.borderColor = '#D1D3D4'
                      e.currentTarget.style.color = '#6b7280'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                  >
                    <BsXLg size={16} />
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      backgroundColor: loading ? '#9ca3af' : '#10B981',
                      border: 'none',
                      color: '#ffffff',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontWeight: '600',
                      fontSize: '15px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      minWidth: '160px',
                      boxShadow: loading ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.2)',
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.backgroundColor = '#059669'
                        e.currentTarget.style.transform = 'translateY(-1px)'
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.currentTarget.style.backgroundColor = '#10B981'
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)'
                      }
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <BsCheckLg size={16} />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </Form>
        </div>
      </div>
    </>
  )
}

export default AddNewStudent

